/**
 * Mock - settings: account, configuration, sentiment tuning log, team, usage.
 * Per-domain mutable state lives in ./db.ts (never in the shared mock/db.ts).
 */
import type { SettingsApi, SettingsSnapshot, TeamMember } from '@/data-sources/contract';
import { HttpError } from '@/lib/errors';
import { newPasswordProblem } from '@/lib/password';
import { simulate } from '../scenarios';
import { settingsDb } from './db';

const copy = (): SettingsSnapshot => structuredClone(settingsDb.snapshot);
const find = (id: string): TeamMember => {
  const member = settingsDb.snapshot.teamMembers.find((candidate) => candidate.id === id);
  if (!member) throw new HttpError(404, `No team member "${id}".`);
  return member;
};
export const mockSettingsApi: SettingsApi = {
  async getSettings(ctx) {
    await simulate('getSettings', ctx.signal, { latency: false });
    return copy();
  },
  async updateSettings(request, ctx) {
    await simulate('updateSettings', ctx.signal, { latency: false });
    if (request.account) Object.assign(settingsDb.snapshot.account, request.account);
    if (request.configuration)
      settingsDb.snapshot.configuration = {
        ...settingsDb.snapshot.configuration,
        ...structuredClone(request.configuration),
      };
    return copy();
  },
  async changePassword({ oldPassword, newPassword }, ctx) {
    await simulate('changePassword', ctx.signal, { latency: false });
    /* The sample workspace has no password to check the old one against; only the rules apply. */
    const problem = newPasswordProblem(oldPassword, newPassword);
    if (problem) throw new HttpError(422, problem);
  },
  async getInviteOptions(ctx) {
    await simulate('getInviteOptions', ctx.signal, { latency: false });
    const { roles, moduleOptions } = settingsDb.snapshot;
    return structuredClone({ roles, moduleOptions });
  },
  async inviteMember(request, ctx) {
    await simulate('inviteMember', ctx.signal, { latency: false });
    const email = request.email.trim().toLowerCase();
    if (!email) throw new HttpError(422, 'An email address is required.');
    if (settingsDb.snapshot.teamMembers.some((member) => member.email === email))
      throw new HttpError(409, 'That person is already a team member.');
    const name = email.split('@')[0]?.replace(/[._-]+/g, ' ') || 'New member';
    const member: TeamMember = {
      id: `invite-${++settingsDb.inviteSequence}`,
      name: name.replace(/\b\w/g, (letter) => letter.toUpperCase()),
      email,
      role: request.role,
      status: 'pending',
      modules: [...request.modules],
    };
    settingsDb.snapshot.teamMembers.push(member);
    return structuredClone(member);
  },
  async updateTeamMember(request, ctx) {
    await simulate('updateTeamMember', ctx.signal, { latency: false });
    const member = find(request.id);
    member.role = request.role;
    member.modules = [...request.modules];
    return structuredClone(member);
  },
  async removeTeamMember({ id }, ctx) {
    await simulate('removeTeamMember', ctx.signal, { latency: false });
    const member = find(id);
    if (member.self) throw new HttpError(422, 'You cannot remove yourself.');
    settingsDb.snapshot.teamMembers = settingsDb.snapshot.teamMembers.filter(
      (item) => item.id !== id,
    );
  },
  async revokeInvitation({ id }, ctx) {
    await simulate('revokeInvitation', ctx.signal, { latency: false });
    const member = find(id);
    if (member.status !== 'pending')
      throw new HttpError(422, 'Only pending invitations can be revoked.');
    settingsDb.snapshot.teamMembers = settingsDb.snapshot.teamMembers.filter(
      (item) => item.id !== id,
    );
  },
};
